import type { RiskGapModel } from '@ctw/shared/api/fhir/models/risk-gap';
import { ContentCard } from '@ctw/shared/components/containers/content-card';
import { DefinitionList } from '@ctw/shared/components/definition-list';
import { HorizontalDivider } from '@ctw/shared/components/horizontal-divider';
import { ConfidenceScale } from '@ctw/shared/components/indicators/confidence-scale';
import { useBuilderConfig } from '@ctw/shared/context/builder-config-context';
import { tw } from '@ctw/shared/utils/tailwind';
import { compact } from 'lodash-es';
import { Fragment } from 'react/jsx-runtime';

export interface RiskGapDetailsCardProps {
  riskGap: RiskGapModel;
}

export const RiskGapDetailsCard = ({ riskGap }: RiskGapDetailsCardProps) => {
  const builderConfig = useBuilderConfig();

  return (
    <ContentCard variant="focal" title="Details">
      <DefinitionList
        className={tw`pt-2 pb-0`}
        items={compact([
          { label: 'HCC', value: riskGap.hccCode },
          { label: 'Source', value: riskGap.gapSourceListDisplay },
          { label: 'Status', value: riskGap.statusDisplay },
          builderConfig.riskGaps?.showConfidenceLevels && {
            label: 'Confidence',
            value: <ConfidenceScale confidenceLevel={riskGap.confidenceRating} />,
          },
          {
            label: 'Matching ICD-10',
            value: (
              <div className={tw`flex flex-col`}>
                <ul className={tw`flex flex-col gap-2`}>
                  {riskGap.icd10Codes.map((code) => (
                    <li
                      key={code.code}
                      style={{ textIndent: `-${(code.code?.length ?? 0) - 1}cw` }}
                      className={tw`leading-none`}
                    >
                      <span className={tw`font-medium text-base text-content-main`}>
                        {code.code ?? ''}
                      </span>
                      {code.display && (
                        <span className={tw`text-base text-content-main`}>
                          &nbsp;{code.display}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
                {riskGap.supersededByHccCodes.length > 0 &&
                riskGap.hierarchicalStatus === 'applied-superseded' ? (
                  <>
                    <HorizontalDivider />
                    <span className={tw`leading-none`}>
                      <span className={tw`text-content-subtle text-xs leading-none`}>
                        Superseded by HCC:&nbsp;
                      </span>
                      {riskGap.supersededByHccCodes.map((hcc, index) => (
                        <Fragment key={hcc.code}>
                          <span className={tw`text-content-main text-xs leading-none`}>
                            {hcc.code}
                            {index < riskGap.supersededByHccCodes.length - 1 ? ', ' : ''}
                          </span>
                        </Fragment>
                      ))}
                    </span>
                  </>
                ) : riskGap.newSupersededByHccCodes.length > 0 ? (
                  <>
                    <HorizontalDivider />
                    <span className={tw`leading-none`}>
                      <span className={tw`text-content-subtle text-xs leading-none`}>
                        Superseded by HCC:&nbsp;
                      </span>
                      {riskGap.newSupersededByHccCodes.map((hcc, index) => (
                        <Fragment key={hcc.code}>
                          <span className={tw`text-content-main text-xs leading-none`}>
                            {hcc.code}
                            {index < riskGap.newSupersededByHccCodes.length - 1 ? ', ' : ''}
                          </span>
                        </Fragment>
                      ))}
                    </span>
                  </>
                ) : (
                  riskGap.newSupersedingHccCodes.length > 0 && (
                    <>
                      <HorizontalDivider />
                      <span className={tw`leading-none`}>
                        <span className={tw`text-content-subtle text-xs leading-none`}>
                          Superseding HCC:&nbsp;
                        </span>
                        {riskGap.newSupersedingHccCodes.map((hcc, index) => (
                          <Fragment key={hcc.code}>
                            <span className={tw`text-content-main text-xs leading-none`}>
                              {hcc.code}
                              {index < riskGap.newSupersedingHccCodes.length - 1 ? ', ' : ''}
                            </span>
                          </Fragment>
                        ))}
                      </span>
                    </>
                  )
                )}
              </div>
            ),
          },
        ])}
      />
    </ContentCard>
  );
};
